import './map-context-menu.css';

import { createContext, ReactNode, useEffect } from 'react';
import { Portal } from 'react-portal';
import { animated } from 'react-spring';

import { useMindmap } from '../../pages/mindmap/use-mindmap';
import { getOutsideOfContextHandler } from '../../utils/getOutsideOfContextHandler';

interface MapContextMenuProps {
  children: ReactNode;
  isOpen: boolean;
}

export const MapContextMenuContext = createContext<{
  onShrinkingAnimationStart: () => void;
  onGrowingAnimationStart: () => void;
}>({
  onShrinkingAnimationStart: getOutsideOfContextHandler('onShrinkingAnimationStart'),
  onGrowingAnimationStart: getOutsideOfContextHandler('onGrowingAnimationStart'),
});

export const MapContextMenu = ({ children, isOpen }: MapContextMenuProps) => {
  const {
    interactableMap: { updateStyle, interactableMapSpring, contextMenuRef },
  } = useMindmap();

  // animate mounting appearance
  useEffect(() => {
    if (isOpen) {
      const contextMenuEl = contextMenuRef.current;
      if (contextMenuEl) {
        contextMenuEl.classList.add('fade-in');
        setTimeout(() => {
          contextMenuEl.classList.remove('fade-in');
        }, 150);
      }
    }
  }, [isOpen]);

  const updatePositioning = (ms: number) => {
    // update map style (positioning of context menu) when context menu changes
    // dimensions and context menu is on bottom of screen
    const bottom = document.getElementById('tool-panel-card')?.getBoundingClientRect().bottom;
    const toolpanelBottomIsOnBottomViewport = bottom && bottom > window.innerHeight - 2;
    if (toolpanelBottomIsOnBottomViewport) {
      let counter = 0;
      let interval = setInterval(() => {
        counter += 1;
        updateStyle();
        if (counter > ms / 10) clearInterval(interval);
      }, 10);
    }
  };

  const onShrinkingAnimationStart = () => updatePositioning(450);

  const onGrowingAnimationStart = () => updatePositioning(1000);

  const [style] = interactableMapSpring || [];

  return (
    <Portal node={document && document.getElementById('app-dialog-mounting-point')}>
      <MapContextMenuContext.Provider
        value={{
          onShrinkingAnimationStart,
          onGrowingAnimationStart,
        }}
      >
        <animated.div
          id="tool-panel-card"
          className="p-contextmenu p-component"
          ref={contextMenuRef}
          style={{
            position: 'absolute',
            left: style?.toolPanelPageX,
            top: style?.toolPanelPageY,
            visibility: isOpen ? undefined : 'hidden',
            zIndex: 10000,
          }}
        >
          {children}
        </animated.div>
      </MapContextMenuContext.Provider>
    </Portal>
  );
};
